import { Page, Text, View, Document, StyleSheet, Image, Font } from '@react-pdf/renderer';
import { AutoText } from '../../../components/pdf/autoText';

// Register Roboto font
Font.register({
  family: 'Roboto',
  fonts: [
    { src: 'https://fonts.gstatic.com/s/roboto/v30/KFOmCnqEu92Fr1Mu4mxP.ttf', fontWeight: 400 },
    { src: 'https://fonts.gstatic.com/s/roboto/v30/KFOlCnqEu92Fr1MmEU9fBBc9.ttf', fontWeight: 700 },
    { src: 'https://fonts.gstatic.com/s/roboto/v30/KFOkCnqEu92Fr1Mu51xIIzc.ttf', fontWeight: 500 },
  ],
});

const styles = StyleSheet.create({
   page: {
    fontFamily: 'Roboto',
    fontSize: 10,
    padding: 40,
    lineHeight: 1.5,
    color: '#333',
    backgroundColor: '#ffffff',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 30,
  },
  companyInfo: {
    width: '60%',
    flexDirection: 'row',
  },
  logo: {
    width: 40,
    objectFit: 'contain',
    marginRight: 12,
    marginTop: 2,
  },
  companyText: {
    flexDirection: 'column',
    justifyContent: 'flex-start',
    marginTop: 20,
  },
  companyName: {
    fontSize: 18,
    fontWeight: 'bold',
    marginTop: 5,
    marginBottom: 4,
  },
  companyDetails: {
    fontSize: 9,
    color: '#666',
    lineHeight: 1.4,
  },
  invoiceHeader: {
    width: '40%',
    alignItems: 'flex-end',
  },
  invoiceTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#4CAF50',
    marginBottom: 8,
  },
  invoiceMeta: {
    fontSize: 9,
    color: '#666',
    marginBottom: 2,
    textAlign: 'right',
  },
  invoiceNumber: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 8,
  },
  divider: {
    height: 1,
    backgroundColor: '#E0E0E0',
    marginBottom: 24,
  },
  twoColumn: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 30,
  },
  column: {
    width: '48%',
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#4CAF50',
    marginBottom: 8,
    letterSpacing: 0.5,
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
    paddingBottom: 4,
  },
  addressBlock: {
    marginBottom: 12,
  },
  label: {
    fontWeight: 'bold',
    fontSize: 9,
    color: '#555',
    marginBottom: 2,
  },
  value: {
    fontSize: 10,
    marginBottom: 8,
  },
  table: {
    marginBottom: 30,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#4CAF50',
    paddingVertical: 8,
    paddingHorizontal: 10,
    color: '#FFFFFF',
    fontSize: 10,
    fontWeight: 'bold',
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 10,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#EEEEEE',
    fontSize: 9,
  },
  col1: { 
    width: '60%',
    paddingRight: 10,
  },
  col2: { 
    width: '20%',
    textAlign: 'right',
    paddingRight: 10,
  },
  col3: { 
    width: '20%',
    textAlign: 'right',
  },
  totalsContainer: {
    width: '35%',
    marginLeft: 'auto',
  },
  totalBox: {
    borderWidth: 1,
    borderColor: '#4CAF50',
    padding: 12,
    backgroundColor: '#F8FDF8',
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  totalLabel: {
    fontSize: 10,
    color: '#555',
  },
  totalValue: {
    fontSize: 10,
    fontWeight: 'bold',
  },
  grandTotal: {
    borderTopWidth: 1,
    borderTopColor: '#4CAF50',
    paddingTop: 8,
    marginTop: 4,
  },
  grandTotalLabel: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#4CAF50',
  },
  grandTotalValue: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#4CAF50',
  },
  paymentInfo: {
    marginTop: 30,
  },
  paymentTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#4CAF50',
    marginBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
    paddingBottom: 4,
  },
  paymentDetail: {
    fontSize: 9,
    marginBottom: 4,
    color: '#555',
  },
  paymentHighlight: {
    color: '#1976D2',
    fontWeight: 'bold',
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 40,
    right: 40,
    fontSize: 8,
    color: '#999',
    textAlign: 'center',
    borderTopWidth: 1,
    borderTopColor: '#EEEEEE',
    paddingTop: 10,
  },
  thankYou: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#4CAF50',
    marginBottom: 4,
  },
});

const InvoicePDF = ({ employeeName, formName = '', amount, transactionID, timestamp }) => {
  const invoiceNumber = `INV-${new Date(timestamp).toISOString().slice(0,19).replace(/[-:T]/g,'')}-${Math.floor(1000 + Math.random() * 9000)}`;
  const date = new Date().toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  const subtotal = parseFloat(amount) || 0;

  const items = [
    {
      description: formName,
      unitPrice: subtotal,
      amount: subtotal,
    },
  ];

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header with logo */}
        <View style={styles.header}>
          <View style={styles.companyInfo}>
            <Image src="/Images/logo.png" style={styles.logo} />
            <View style={styles.companyText}>
              <Text style={styles.companyName}>HIMANSHU SALARY CORNER</Text>
              <Text style={styles.companyDetails}>
                154, Guruwar Peth, Satara, Maharashtra 415001{"\n"}
                Contact: +91 95294 02894 | admin@himanshusalarycorner.in
              </Text>
            </View>
          </View>
          <View style={styles.invoiceHeader}>
            <Text style={styles.invoiceTitle}>INVOICE</Text>
            <Text style={styles.invoiceNumber}>{invoiceNumber}</Text>
            <Text style={styles.invoiceMeta}>Date: {date}</Text>
            <Text style={styles.invoiceMeta}>Transaction ID: {transactionID}</Text>
          </View>
        </View>

        <View style={styles.divider} />

        {/* From / To */}
        <View style={styles.twoColumn}>
          <View style={styles.column}>
            <Text style={styles.sectionTitle}>From</Text>
            <View style={styles.addressBlock}>
              <Text style={styles.label}>Business Name</Text>
              <Text style={styles.value}>Himanshu Salary Corner</Text>
              
              <Text style={styles.label}>Address</Text>
              <Text style={styles.value}>154, Guruwar Peth{"\n"}Satara, Maharashtra 415001{"\n"}India</Text>
            </View>
          </View>
          <View style={styles.column}>
            <Text style={styles.sectionTitle}>Bill To</Text>
            <View style={styles.addressBlock}>
              <Text style={styles.label}>Client Name</Text>
              <AutoText style={styles.value}>{employeeName}</AutoText>
            </View>
          </View>
        </View>

        {/* Items Table */}
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={styles.col1}>DESCRIPTION</Text>
            <Text style={styles.col2}>UNIT PRICE</Text>
            <Text style={styles.col3}>AMOUNT</Text>
          </View>
          {items.map((item, index) => (
            <View key={index} style={styles.tableRow}>
              <Text style={styles.col1}>{item.description}</Text>
              <AutoText style={styles.col2}>₹{item.unitPrice.toFixed(2)}</AutoText>
              <AutoText style={styles.col3}>₹{item.amount.toFixed(2)}</AutoText>
            </View>
          ))}
        </View>

        {/* Totals */}
        <View style={styles.totalsContainer}>
          <View style={styles.totalBox}>
            <View style={[styles.totalRow, styles.grandTotal]}>
              <Text style={styles.grandTotalLabel}>TOTAL DUE</Text>
              <AutoText style={styles.grandTotalValue}>₹{subtotal.toFixed(2)}</AutoText>
            </View>
          </View>
        </View>

        {/* Payment Information */}
        <View style={styles.paymentInfo}>
          <Text style={styles.paymentTitle}>PAYMENT INFORMATION</Text>
          <Text style={styles.paymentDetail}>Bank: <Text style={styles.paymentHighlight}>HDFC Bank</Text></Text>
          <Text style={styles.paymentDetail}>Account Holder: <Text style={styles.paymentHighlight}>Himanshu Salary Corner</Text></Text>
          <Text style={styles.paymentDetail}>UPI ID: <Text style={styles.paymentHighlight}>Vyapar.172720938288@hdfcbank</Text></Text>
          <Text style={[styles.paymentDetail, { marginTop: 8 }]}>
            Please include invoice number ({invoiceNumber}) in your payment reference.
          </Text>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.thankYou}>Thank you for your business!</Text>
          <Text>This is a computer generated invoice and does not require a physical signature.</Text>
          <Text>For any queries, please contact us at admin@himanshusalarycorner.in or +91 95294 02894</Text>
        </View>
      </Page>
    </Document>
  );
};

export default InvoicePDF;